import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';

/**
 * Plain React-controlled modal (conditional render + Bootstrap's own
 * .modal/.modal-backdrop CSS classes), not Bootstrap's JS Modal component -
 * avoids any interop friction between React's re-renders and Bootstrap's
 * direct DOM manipulation of the modal/backdrop classes, at the cost of a
 * couple lines of manual show/hide logic this component owns itself. The
 * navbar's mobile toggle (App.jsx) still uses Bootstrap's own JS for its
 * collapse, since that one has no form state to coordinate with.
 */
export default function ChangePasswordModal({ show, onClose }) {
  const { changePassword } = useAuth();

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!show) {
    return null;
  }

  function reset() {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(false);
    setSubmitting(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(oldPassword, newPassword);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Could not change password.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="modal-backdrop show" onClick={handleClose} />
      <div className="modal show d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0">
            <div className="modal-header">
              <h1 className="modal-title h5">Change password</h1>
              <button type="button" className="btn-close" aria-label="Close" onClick={handleClose} />
            </div>

            {success ? (
              <div className="modal-body">
                <p className="text-success mb-0">Password changed successfully.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label small">Current password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      autoFocus
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small">New password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={12}
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="mb-1">
                    <label className="form-label small">Confirm new password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={12}
                      autoComplete="new-password"
                    />
                  </div>
                  {error && <p className="text-danger small mb-0 mt-2">{error}</p>}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={handleClose}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Changing…' : 'Change password'}
                  </button>
                </div>
              </form>
            )}

            {success && (
              <div className="modal-footer">
                <button type="button" className="btn btn-primary" onClick={handleClose}>
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
